/**
 * ScrollAnimations — CHI Alliance
 * Feng shui fluid motion component library.
 * All animations respect prefers-reduced-motion.
 *
 * Components:
 *   <FadeUp>       — gentle upward fade (most common, use for text blocks, cards)
 *   <FlowIn>       — slides in from left or right with fade (for side-by-side content)
 *   <ScaleReveal>  — subtle scale from 0.94 → 1 with fade (for images, big cards)
 *   <StaggerGroup> — wraps children and staggers their FadeUp by index
 *   <FlowLine>     — animated horizontal rule that draws itself in
 */
import React, { useRef, useEffect, useState, Children, cloneElement, isValidElement } from "react";

// ─── Shared easing ────────────────────────────────────────────────────────────
const EASE_OUT = "cubic-bezier(0.23, 1, 0.32, 1)";

// ─── Base hook ────────────────────────────────────────────────────────────────
/**
 * Reveals content shortly *before* it scrolls into view.
 *
 * The margin used to be `0px 0px -50px 0px`, which shrinks the observer's bottom
 * edge 50px inside the viewport: an element had to be visibly on screen before it
 * was allowed to start animating. Combined with a 10% threshold, content you could
 * already see was still at opacity 0, then faded in late — which reads as flashing
 * and pop-in while scrolling, and is worse the faster you scroll or the slower the
 * image inside is to load.
 *
 * A positive bottom margin extends the observer *past* the viewport instead, so
 * the animation starts while the element is still below the fold and has finished
 * by the time it is actually looked at. 20% of viewport height rather than a fixed
 * pixel value, so the lead time scales with the screen — 50px is most of a phone
 * scroll and almost nothing on a desktop monitor.
 *
 * The threshold drops to 0 for the same reason: requiring 10% of a tall section to
 * be inside the box delays a full-height element by most of a screen.
 */
function useReveal(threshold = 0, rootMargin = "0px 0px 20% 0px") {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Anything already on screen at mount is revealed immediately, with no
    // observer round trip. Above-the-fold content should never animate in — the
    // visitor is looking at it before any scrolling happens.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setVisible(true);
      return;
    }

    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(el); } },
      { threshold, rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold, rootMargin]);

  return { ref, visible };
}

// ─── Reduced motion check ─────────────────────────────────────────────────────
function useReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduced;
}

// ─── FadeUp ───────────────────────────────────────────────────────────────────
interface FadeUpProps {
  children: React.ReactNode;
  delay?: number;        // ms
  duration?: number;     // ms
  distance?: number;     // px upward travel
  className?: string;
  style?: React.CSSProperties;
  as?: keyof React.JSX.IntrinsicElements;
}

export function FadeUp({
  children,
  delay = 0,
  duration = 700,
  distance = 28,
  className,
  style,
  as: Tag = "div",
}: FadeUpProps) {
  const { ref, visible } = useReveal(0.1);
  const reduced = useReducedMotion();

  const baseStyle: React.CSSProperties = {
    opacity: reduced || visible ? 1 : 0,
    transform: reduced || visible ? "translateY(0px)" : `translateY(${distance}px)`,
    transition: reduced
      ? "none"
      : `opacity ${duration}ms ${EASE_OUT} ${delay}ms, transform ${duration}ms ${EASE_OUT} ${delay}ms`,
    willChange: "opacity, transform",
    ...style,
  };

  return (
    // @ts-ignore — dynamic tag
    <Tag ref={ref} className={className} style={baseStyle}>
      {children}
    </Tag>
  );
}

// ─── FlowIn ───────────────────────────────────────────────────────────────────
interface FlowInProps {
  children: React.ReactNode;
  direction?: "left" | "right";
  delay?: number;
  duration?: number;
  distance?: number;
  className?: string;
  style?: React.CSSProperties;
}

export function FlowIn({
  children,
  direction = "left",
  delay = 0,
  duration = 750,
  distance = 36,
  className,
  style,
}: FlowInProps) {
  const { ref, visible } = useReveal(0.1);
  const reduced = useReducedMotion();
  const tx = direction === "left" ? -distance : distance;

  const baseStyle: React.CSSProperties = {
    opacity: reduced || visible ? 1 : 0,
    transform: reduced || visible ? "translateX(0px)" : `translateX(${tx}px)`,
    transition: reduced
      ? "none"
      : `opacity ${duration}ms ${EASE_OUT} ${delay}ms, transform ${duration}ms ${EASE_OUT} ${delay}ms`,
    willChange: "opacity, transform",
    ...style,
  };

  return (
    <div ref={ref} className={className} style={baseStyle}>
      {children}
    </div>
  );
}

// ─── ScaleReveal ──────────────────────────────────────────────────────────────
interface ScaleRevealProps {
  children: React.ReactNode;
  delay?: number;
  duration?: number;
  className?: string;
  style?: React.CSSProperties;
}

export function ScaleReveal({
  children,
  delay = 0,
  duration = 800,
  className,
  style,
}: ScaleRevealProps) {
  const { ref, visible } = useReveal(0.08);
  const reduced = useReducedMotion();

  const baseStyle: React.CSSProperties = {
    opacity: reduced || visible ? 1 : 0,
    transform: reduced || visible ? "scale(1)" : "scale(0.94)",
    transition: reduced
      ? "none"
      : `opacity ${duration}ms ${EASE_OUT} ${delay}ms, transform ${duration}ms ${EASE_OUT} ${delay}ms`,
    willChange: "opacity, transform",
    ...style,
  };

  return (
    <div ref={ref} className={className} style={baseStyle}>
      {children}
    </div>
  );
}

// ─── StaggerGroup ─────────────────────────────────────────────────────────────
interface StaggerGroupProps {
  children: React.ReactNode;
  staggerMs?: number;    // delay between each child
  baseDelay?: number;    // initial delay before first child
  distance?: number;
  duration?: number;
  className?: string;
  style?: React.CSSProperties;
}

export function StaggerGroup({
  children,
  staggerMs = 80,
  baseDelay = 0,
  distance = 24,
  duration = 650,
  className,
  style,
}: StaggerGroupProps) {
  const { ref, visible } = useReveal(0.08);
  const reduced = useReducedMotion();

  const items = Children.toArray(children);

  return (
    <div ref={ref} className={className} style={style}>
      {items.map((child, i) => {
        const delay = baseDelay + i * staggerMs;
        const childStyle: React.CSSProperties = {
          opacity: reduced || visible ? 1 : 0,
          transform: reduced || visible ? "translateY(0px)" : `translateY(${distance}px)`,
          transition: reduced
            ? "none"
            : `opacity ${duration}ms ${EASE_OUT} ${delay}ms, transform ${duration}ms ${EASE_OUT} ${delay}ms`,
          willChange: "opacity, transform",
        };
        if (isValidElement(child)) {
          const existing = (child.props as any).style || {};
          return cloneElement(child as React.ReactElement<any>, {
            key: i,
            style: { ...existing, ...childStyle },
          });
        }
        return (
          <div key={i} style={childStyle}>
            {child}
          </div>
        );
      })}
    </div>
  );
}

// ─── FlowLine ─────────────────────────────────────────────────────────────────
interface FlowLineProps {
  color?: string;
  delay?: number;
  duration?: number;
  height?: number;
  style?: React.CSSProperties;
}

export function FlowLine({
  color = "rgba(233,30,140,0.4)",
  delay = 0,
  duration = 900,
  height = 1,
  style,
}: FlowLineProps) {
  const { ref, visible } = useReveal(0.2);
  const reduced = useReducedMotion();

  return (
    <div
      ref={ref}
      style={{
        height,
        background: color,
        transformOrigin: "left center",
        transform: reduced || visible ? "scaleX(1)" : "scaleX(0)",
        transition: reduced ? "none" : `transform ${duration}ms ${EASE_OUT} ${delay}ms`,
        willChange: "transform",
        ...style,
      }}
    />
  );
}

// ─── SectionReveal ────────────────────────────────────────────────────────────
// Wraps an entire section with a subtle fade + upward drift
interface SectionRevealProps {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  style?: React.CSSProperties;
}

export function SectionReveal({ children, delay = 0, className, style }: SectionRevealProps) {
  return (
    <FadeUp delay={delay} duration={800} distance={20} className={className} style={style}>
      {children}
    </FadeUp>
  );
}
